import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, Check, Eye, ChevronLeft, ChevronRight, ShieldCheck } from 'lucide-react';
import { apiClient, publicApi, useAuth, formatApiError } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { cartStorage } from '../utils/cartStorage';
import { useLanguage } from '../context/LanguageContext';
import StructuredData, { buildProductSchema } from '../components/StructuredData';
import ProductBadge from '../components/ProductBadge';

export default function ProductDetail() {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedColor, setSelectedColor] = useState(0);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedLensOption, setSelectedLensOption] = useState(null);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [packItems, setPackItems] = useState(["Lunettes Joey Cyan", "Étui pliable premium", "Chiffon de nettoyage", "Câble de charge", "Manuel utilisateur"]);
  const [returnsEnabled, setReturnsEnabled] = useState(true);
  const [lensConfigEnabled, setLensConfigEnabled] = useState(true);
  
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isPro } = useAuth();
  const [searchParams] = useSearchParams();
  const { t, tc, language } = useLanguage();

  useEffect(() => {
    publicApi.get('/settings/pack-contents').then(({ data }) => {
      if (data?.items?.length > 0) setPackItems(data.items);
    }).catch(() => {});
    publicApi.get('/settings/public').then(({ data }) => {
      if (typeof data?.returns_14_enabled === 'boolean') {
        setReturnsEnabled(data.returns_14_enabled);
      }
      if (typeof data?.lens_config_enabled === 'boolean') {
        setLensConfigEnabled(data.lens_config_enabled);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const fetchProduct = async () => {
      if (!slug) {
        navigate('/collection');
        return;
      }
      try {
        // For SEO: always fetch the French version (no lang param) — meta tags must stay in French.
        // The UI translation for the user is handled separately by useLanguage.
        const langParam = language !== 'fr' ? `?lang=${language}` : '';
        const { data } = await publicApi.get(`/products/slug/${slug}${langParam}`);
        setProduct(data);
        // Note: do NOT overwrite document.title here — pre-rendered HTML already has the correct French title.
        
        // Set default lens option if has_lens_config
        if (data.has_lens_config && data.lens_options?.length > 0) {
          setSelectedLensOption(data.lens_options[0]);
        }
        
        // Check if there's a color parameter in URL and select that color
        const colorParam = searchParams.get('color');
        if (colorParam && data.colors && data.colors.length > 0) {
          const colorIndex = data.colors.findIndex(c => {
            const colorSlug = typeof c === 'string' ? c.toLowerCase() : (c.slug || c.name).toLowerCase();
            return colorSlug === colorParam.toLowerCase();
          });
          
          if (colorIndex !== -1) {
            setSelectedColor(colorIndex);
          }
        }
      } catch (err) {
        console.error('Error fetching product:', err);
        toast.error(t('productDetail.notFound'));
        navigate('/collection');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, navigate, searchParams]);

  // Reset selected image when color changes
  useEffect(() => {
    setSelectedImage(0);
  }, [selectedColor]);

  // Get images for current color - use useMemo to ensure proper updates
  const allImages = useMemo(() => {
    if (!product) return [];
    const currentColor = product.colors?.[selectedColor];
    const currentColorName = typeof currentColor === 'string' ? currentColor : currentColor?.name;
    
    // Check if there are color-specific images
    if (currentColorName && product.color_images && product.color_images[currentColorName]?.length > 0) {
      return product.color_images[currentColorName];
    }
    
    // Fallback to main images + gallery
    return [...(product.images || []), ...(product.gallery_urls || [])].filter(Boolean);
  }, [product, selectedColor]);

  // Combined media gallery: videos first, then images. Used for main viewer + thumbnails.
  const allMedia = useMemo(() => {
    const vids = (product?.videos || []).map((src) => ({ type: 'video', src }));
    const imgs = allImages.map((src) => ({ type: 'image', src }));
    return [...vids, ...imgs];
  }, [product?.videos, allImages]);

  // Resolve base (public) and pro prices for the currently selected color.
  // A color may override product-level prices via `price` and `pro_price`.
  // If the override is missing or not a positive number, we fall back to the product's price.
  const getEffectivePrices = () => {
    if (!product) return { publicPrice: 0, proPrice: 0 };
    const currentColor = product.colors?.[selectedColor];
    const colorObj = typeof currentColor === 'object' && currentColor !== null ? currentColor : null;
    const rawColorPublic = colorObj?.price;
    const rawColorPro = colorObj?.pro_price;
    const colorPublic = (rawColorPublic !== undefined && rawColorPublic !== null && rawColorPublic !== '' && !isNaN(Number(rawColorPublic)) && Number(rawColorPublic) > 0)
      ? Number(rawColorPublic)
      : null;
    const colorPro = (rawColorPro !== undefined && rawColorPro !== null && rawColorPro !== '' && !isNaN(Number(rawColorPro)) && Number(rawColorPro) > 0)
      ? Number(rawColorPro)
      : null;
    return {
      publicPrice: colorPublic ?? product.price,
      proPrice: colorPro ?? product.pro_price ?? null,
    };
  };

  // Calculate total price
  const getPrice = () => {
    if (!product) return 0;
    const { publicPrice, proPrice } = getEffectivePrices();
    let basePrice = (isPro && proPrice) ? proPrice : publicPrice;
    let price = basePrice;
    if (selectedLensOption && selectedLensOption.price_modifier) {
      price += selectedLensOption.price_modifier;
    }
    return price;
  };

  // Calculate total price with quantity
  const getTotalPrice = () => {
    return (getPrice() * qty).toFixed(2);
  };

  // Get base price (without lens options)
  const getBasePrice = () => {
    if (!product) return 0;
    const { publicPrice, proPrice } = getEffectivePrices();
    return (isPro && proPrice) ? proPrice : publicPrice;
  };

  // Calculate savings for pros (based on the selected color)
  const getSavings = () => {
    if (!product || !isPro) return 0;
    const { publicPrice, proPrice } = getEffectivePrices();
    if (!proPrice) return 0;
    return (publicPrice - proPrice).toFixed(2);
  };

  const getSavingsPercentage = () => {
    if (!product || !isPro) return 0;
    const { publicPrice, proPrice } = getEffectivePrices();
    if (!proPrice || !publicPrice) return 0;
    return Math.round(((publicPrice - proPrice) / publicPrice) * 100);
  };

  const handleAddToCart = async () => {
    if (!product) return;
    
    // Require lens option for optical products (hidden for pro users who skip lens config)
    if (lensConfigEnabled && product.has_lens_config && !selectedLensOption && !isPro) {
      toast.error(t('productDetail.chooseLens'));
      return;
    }
    
    setAddingToCart(true);
    
    try {
      const currentColor = product.colors?.[selectedColor];
      const colorName = typeof currentColor === 'string' ? currentColor : currentColor?.name;
      
      // Récupérer l'image correspondant à la couleur sélectionnée
      // Priorité: color_images de la couleur > images du produit
      let productImage = null;
      
      // D'abord vérifier color_images pour la couleur sélectionnée
      if (colorName && product.color_images?.[colorName]?.length > 0) {
        productImage = product.color_images[colorName][0];
      }
      
      // Sinon utiliser la première image du produit
      if (!productImage) {
        productImage = product.images?.[0] || null;
      }
      
      // Calculer le prix avec le modificateur de verres si applicable
      const { publicPrice, proPrice } = getEffectivePrices();
      let basePrice = (isPro && proPrice) ? proPrice : publicPrice;
      let lensModifier = selectedLensOption?.price_modifier || 0;
      let finalPrice = basePrice + lensModifier;
      
      const cartItem = {
        product_id: product.id,
        product_name: product.name,
        product_image: productImage,
        price: finalPrice,
        base_price: basePrice,
        lens_price_modifier: lensModifier,
        quantity: qty,
        color: colorName || null,
        lens_option: selectedLensOption?.name || null
      };
      
      if (isAuthenticated) {
        // Utilisateur connecté : utiliser l'API
        await apiClient.post('/cart', cartItem);
        // Déclencher l'événement pour mettre à jour le compteur
        window.dispatchEvent(new Event('cart-updated'));
      } else {
        // Visiteur non connecté : utiliser localStorage
        cartStorage.addItem(cartItem);
        // Déclencher un événement personnalisé pour mettre à jour le compteur du panier
        window.dispatchEvent(new Event('cart-updated'));
      }
      
      setAdded(true);
      toast.success(t('productDetail.addedToCart'));
      setTimeout(() => setAdded(false), 2500);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAddingToCart(false);
    }
  };

  const colorMap = {
    "noir": "#111111",
    "blanc": "#f0f0f0",
    "doré": "#d4af37",
    "argenté": "#c0c0c0",
    "argent": "#c0c0c0",
    "or rose": "#e8b4b8",
    "bleu": "#2563eb",
    "bleu dégradé": "linear-gradient(135deg, #1e40af, #60a5fa)",
    "gris": "#9ca3af",
    "vert": "#16a34a",
    "rose": "#ec4899",
    "rouge": "#dc2626",
    "transparent": "linear-gradient(135deg, #e0f4ff, #ffffff)",
    "transparent bleuté": "linear-gradient(135deg, #bfdbfe, #eff6ff)",
    "écaille": "linear-gradient(135deg, #92400e, #d97706, #fef3c7)",
    "noir mat": "#1a1a1a",
    "noir/rouge": "linear-gradient(135deg, #111, #dc2626)",
    "blanc/bleu": "linear-gradient(135deg, #f0f0f0, #2563eb)",
    "gris/jaune": "linear-gradient(135deg, #9ca3af, #facc15)",
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#00aaff] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center text-gray-500">
        {t("productDetail.notFound")}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-gray-900 px-4 md:px-6 py-6 md:py-10" data-testid="product-detail-page">
      <StructuredData data={buildProductSchema(product, { slug })} />
      <div className="max-w-6xl mx-auto">
        <Link to="/collection" className="inline-flex items-center gap-2 text-gray-400 hover:text-[#00aaff] text-sm mb-6 md:mb-10 transition-colors">
          <ArrowLeft size={16} /> {t('productDetail.backToCollection')}
        </Link>

        <div className="grid md:grid-cols-2 gap-6 md:gap-12 lg:gap-20">
          {/* Image */}
          <div className="space-y-4">
            <div className="relative aspect-square rounded-3xl overflow-hidden bg-gray-50 border border-gray-100">
              {allMedia.length > 0 ? (
                <>
                  {allMedia[selectedImage]?.type === 'video' ? (
                    <video
                      key={allMedia[selectedImage].src}
                      src={allMedia[selectedImage].src}
                      controls
                      playsInline
                      className="w-full h-full object-contain bg-black"
                      data-testid="product-video-player"
                    />
                  ) : (
                    <img
                      src={allMedia[selectedImage].src}
                      alt={product.name}
                      className="w-full h-full object-contain"
                    />
                  )}
                  {allMedia.length > 1 && (
                    <>
                      <button 
                        onClick={() => setSelectedImage(i => (i - 1 + allMedia.length) % allMedia.length)}
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/90 rounded-full shadow flex items-center justify-center hover:bg-white transition-all z-10"
                      >
                        <ChevronLeft size={18} className="text-gray-700" />
                      </button>
                      <button 
                        onClick={() => setSelectedImage(i => (i + 1) % allMedia.length)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/90 rounded-full shadow flex items-center justify-center hover:bg-white transition-all z-10"
                      >
                        <ChevronRight size={18} className="text-gray-700" />
                      </button>
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                        {allMedia.map((_, i) => (
                          <button 
                            key={i} 
                            onClick={() => setSelectedImage(i)}
                            className={`w-1.5 h-1.5 rounded-full transition-all ${selectedImage === i ? "bg-[#00aaff] w-3" : "bg-gray-300"}`} 
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-300">
                  <Eye size={80} />
                </div>
              )}
              {product.is_new && (
                <span className="absolute top-5 left-5 bg-[#00aaff] text-white text-xs font-bold px-3 py-1 rounded-full z-10">NEW</span>
              )}
            </div>
            
            {/* Thumbnails */}
            {allMedia.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-1">
                {allMedia.map((m, i) => (
                  <button 
                    key={i} 
                    onClick={() => setSelectedImage(i)}
                    className={`relative w-16 h-16 rounded-xl overflow-hidden border-2 shrink-0 transition-all ${selectedImage === i ? "border-[#00aaff]" : "border-transparent hover:border-gray-300"}`}
                  >
                    {m.type === 'video' ? (
                      <>
                        <video src={m.src} className="w-full h-full object-cover bg-black" muted preload="metadata" playsInline />
                        <span className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z" /></svg>
                        </span>
                      </>
                    ) : (
                      <img src={m.src} alt={`vue ${i+1}`} className="w-full h-full object-cover" />
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* {t('productDetail.packContent')} */}
            <div className="bg-[#f5f7fa] border border-[#e8ecf0] rounded-2xl p-4">
              <p className="text-sm font-bold text-gray-800 mb-3">📦 {t('productDetail.packContent')}</p>
              <ul className="space-y-2">
                {packItems.map(item => (
                  <li key={item} className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#00aaff] shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Details */}
          <div>
            <div className="mb-2">
              <span className="text-xs text-[#00aaff] uppercase tracking-widest font-semibold capitalize">
                {tc(product.category)}
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-black mb-1" data-testid="product-name">{product.name}</h1>
            {product.subtitle && <p className="text-gray-400 mb-4">{tc(product.subtitle)}</p>}

            {product.blue_light_filter && (
              <div className="inline-flex items-center gap-1.5 bg-violet-50 border border-violet-200 rounded-full px-3 py-1.5 mb-4" data-testid="blue-light-badge">
                <ShieldCheck size={14} className="text-violet-500 shrink-0" />
                <span className="text-xs text-violet-700 font-semibold">Filtre lumière bleue intégré</span>
              </div>
            )}
            {product.custom_badge?.enabled && product.custom_badge?.text && (
              <div className="mb-4">
                <ProductBadge badge={product.custom_badge} size="md" />
              </div>
            )}

            {/* Prix */}
            <div className="mb-4">
              {(() => {
                const { publicPrice, proPrice } = getEffectivePrices();
                return isPro && proPrice ? (
                <div>
                  {/* Prix Pro */}
                  <div className="flex items-baseline gap-3 mb-2">
                    <div className="text-4xl font-black text-[#00aaff]" data-testid="product-price">
                      {getPrice()}€ <span className="text-lg font-semibold text-amber-700">HT</span>
                    </div>
                    {getSavings() > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-lg text-gray-400 line-through">{publicPrice}€</span>
                        <span className="inline-flex items-center gap-1 bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                          -{getSavingsPercentage()}% PRO
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Détail des prix */}
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{t('productDetail.publicPrice')} (TTC) :</span>
                      <span className="font-semibold text-gray-900">{publicPrice}€</span>
                    </div>
                    <div className="flex justify-between text-sm border-t border-amber-200 pt-2">
                      <span className="text-amber-900 font-bold">{t('productDetail.proPrice')} ⭐ (HT) :</span>
                      <span className="font-black text-amber-900">{getBasePrice()}€ HT</span>
                    </div>
                    <div className="flex justify-between text-sm border-t border-amber-200 pt-2">
                      <span className="text-gray-600">TVA (20%) :</span>
                      <span className="font-semibold text-gray-900">{(getBasePrice() * 0.20).toFixed(2)}€</span>
                    </div>
                    <div className="flex justify-between text-sm border-t border-amber-200 pt-2">
                      <span className="text-amber-900 font-bold">Prix Pro (TTC) :</span>
                      <span className="font-black text-amber-900">{(getBasePrice() * 1.20).toFixed(2)}€</span>
                    </div>
                    {selectedLensOption && selectedLensOption.price_modifier > 0 && (
                      <div className="flex justify-between text-sm border-t border-amber-200 pt-2">
                        <span className="text-gray-600">+ Option verres :</span>
                        <span className="font-semibold text-gray-900">{selectedLensOption.price_modifier}€</span>
                      </div>
                    )}
                    {getSavings() > 0 && (
                      <div className="flex justify-between text-sm border-t-2 border-amber-300 pt-2">
                        <span className="text-green-700 font-bold">✓ {t('productDetail.youSave')} :</span>
                        <span className="font-black text-green-700">{getSavings()}€</span>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-4xl font-black text-[#00aaff]" data-testid="product-price">
                  {getPrice()}€
                  {selectedLensOption && selectedLensOption.price_modifier > 0 && (
                    <span className="text-lg text-gray-400 font-normal ml-2">
                      (base {publicPrice}€ + verres {selectedLensOption.price_modifier}€)
                    </span>
                  )}
                </div>
              );
              })()}
            </div>

            {/* Colors */}
            {/* Color Selection with Swatches */}
            {product.colors?.length > 0 && (
              <div className="mb-6">
                <p className="text-sm text-gray-500 mb-3 font-medium">
                  {t('productDetail.color')} : <span className="text-gray-900 font-semibold">
                    {typeof product.colors[selectedColor] === 'string' 
                      ? product.colors[selectedColor] 
                      : product.colors[selectedColor]?.name}
                  </span>
                </p>
                <div className="flex flex-wrap gap-3 items-center">
                  {product.colors.map((color, i) => {
                    // Handle both old string format and new object format
                    const colorName = typeof color === 'string' ? color : color.name;
                    const colorValue = typeof color === 'string' 
                      ? (colorMap[color.toLowerCase()] || "#888888")
                      : color.value;
                    
                    const isLight = ["#FFFFFF", "#FFFFFFFF", "#FFF"].includes(colorValue.toUpperCase());
                    const hasImages = product.color_images && product.color_images[colorName]?.length > 0;
                    
                    return (
                      <button
                        key={i}
                        onClick={() => setSelectedColor(i)}
                        title={colorName}
                        className="relative group flex flex-col items-center gap-2 transition-all"
                      >
                        <div
                          className={`w-12 h-12 rounded-full transition-all duration-200 ${
                            selectedColor === i
                              ? "ring-4 ring-[#00aaff] ring-offset-2 scale-110 shadow-lg"
                              : "ring-2 ring-gray-200 hover:scale-105 hover:ring-[#00aaff]/40 hover:shadow-md"
                          } ${isLight ? "border border-gray-300" : ""}`}
                          style={{ backgroundColor: colorValue }}
                        >
                          <div className="w-full h-full rounded-full flex items-center justify-center">
                            {selectedColor === i && (
                              <Check size={18} className={isLight ? "text-gray-700" : "text-white"} strokeWidth={3} />
                            )}
                          </div>
                        </div>
                        <span className={`text-xs font-semibold leading-tight text-center max-w-[60px] truncate transition-colors ${
                          selectedColor === i ? "text-[#00aaff]" : "text-gray-500"
                        }`}>
                          {colorName}
                        </span>
                        {hasImages && (
                          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white" title="Photos disponibles"></span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Lens Configuration */}
            {lensConfigEnabled && product.has_lens_config && product.lens_options?.length > 0 && !isPro && (
              <div className="mb-6">
                <p className="text-sm text-gray-500 mb-3 font-medium">
                  {t('productDetail.lensConfig')} : <span className="text-gray-900 font-semibold">{selectedLensOption?.name || t('productDetail.notSelected')}</span>
                </p>
                <div className="space-y-2">
                  {product.lens_options.map((opt, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedLensOption(opt)}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                        selectedLensOption?.name === opt.name
                          ? "border-[#00aaff] bg-[#e0f4ff]/50"
                          : "border-gray-200 hover:border-[#00aaff]/40"
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            {opt.emoji && <span className="text-lg leading-none">{opt.emoji}</span>}
                            <p className="font-semibold text-gray-900">{opt.name}</p>
                            {opt.is_reimbursable && (
                              <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                                <ShieldCheck size={10} /> {t("productDetail.reimbursed")}
                              </span>
                            )}
                          </div>
                          {opt.description && (
                            <p className="text-sm text-gray-500 mt-1">{opt.description}</p>
                          )}
                          {opt.tags && opt.tags.length > 0 && (
                            <div className="flex gap-1 mt-2">
                              {opt.tags.map(tag => (
                                <span key={tag} className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {!opt.hide_price_label && (
                            <span className={`font-bold ${opt.price_modifier > 0 ? "text-[#00aaff]" : "text-green-600"}`}>
                              {opt.price_modifier > 0 ? `+${opt.price_modifier}€` : t("productDetail.included")}
                            </span>
                          )}
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            selectedLensOption?.name === opt.name
                              ? "border-[#00aaff] bg-[#00aaff]"
                              : "border-gray-300"
                          }`}>
                            {selectedLensOption?.name === opt.name && (
                              <Check size={12} className="text-white" />
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                {selectedLensOption?.is_reimbursable && (
                  <>
                    <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
                      <span className="text-sm text-gray-700 font-medium">Prix avec verres sélectionnés :</span>
                      <span className="flex items-baseline gap-2">
                        <span className="text-2xl font-black text-[#00aaff]">{getTotalPrice()}€</span>
                        <span className="text-sm font-semibold text-green-600">Remboursable</span>
                      </span>
                    </div>

                    <div className="mt-3 rounded-2xl border border-green-200 bg-green-50 px-4 py-3">
                      <p className="text-sm text-gray-700 leading-relaxed">
                        <span className="mr-1">📋</span>
                        Pour la prise en charge, envoyez votre ordonnance + carte mutuelle (avec attestation carte vitale) ou attestation CMU-C à{' '}
                        <a href="mailto:contact@joeycyan.com" className="text-[#00aaff] font-semibold underline">contact@joeycyan.com</a>
                      </p>
                    </div>

                    <div className="mt-3 rounded-2xl border border-gray-200 bg-white px-4 py-3">
                      <p className="text-sm text-gray-700 leading-relaxed">
                        <span className="mr-1">🌞</span>
                        Vous souhaitez des verres <strong>solaires, anti-lumière bleue ou photochromiques</strong> ? Mentionnez-le dans votre email à{' '}
                        <a href="mailto:contact@joeycyan.com" className="text-[#00aaff] font-semibold underline">contact@joeycyan.com</a>
                        {' '}et nos opticiens partenaires s'en occuperont.
                      </p>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Qty + Add */}
            <div className="flex items-center gap-3 mb-6">
              <div className="flex items-center gap-3 bg-gray-100 border border-gray-200 rounded-full px-4 py-2">
                <button onClick={() => setQty(q => Math.max(1, q - 1))} className="text-gray-400 hover:text-gray-900 w-5 text-center font-bold">−</button>
                <span className="w-5 text-center font-semibold text-gray-900" data-testid="quantity">{qty}</span>
                <button onClick={() => setQty(q => q + 1)} className="text-gray-400 hover:text-gray-900 w-5 text-center font-bold">+</button>
              </div>
              <button 
                onClick={handleAddToCart} 
                disabled={addingToCart || added}
                data-testid="add-to-cart-btn"
                className={`flex-1 flex items-center justify-center gap-2 py-3.5 rounded-full text-sm font-bold transition-all ${
                  added
                    ? "bg-green-500 text-white"
                    : "bg-[#00aaff] text-white hover:bg-[#0088dd]"
                }`}
              >
                {added ? <><Check size={16} /> {t('productDetail.addedToCart')}</> : addingToCart ? "..." : <><ShoppingBag size={16} /> {t('productDetail.addToCart')} • {getTotalPrice()}€</>}
              </button>
            </div>

            <div className="flex flex-wrap gap-4 text-xs text-gray-500 mb-6">
              {returnsEnabled && !isPro && (
                <span data-testid="returns-14-badge">✓ {t('productDetail.returns14')}</span>
              )}
            </div>

            {/* Description */}
            {product.description && (
              <p className="text-sm text-gray-500 leading-relaxed mb-6">{product.description}</p>
            )}

            {/* Features */}
            {false && product.features && (
              <div className="bg-[#f5f7fa] border border-[#e8ecf0] rounded-2xl p-4">
                <p className="text-sm font-bold text-gray-800 mb-3">⚡ {t('productDetail.techSpecs')}</p>
                <ul className="space-y-2">
                  {(Array.isArray(product.features) ? product.features : product.features.split(',').map(f => f.trim())).filter(f => f).map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#00aaff] shrink-0 mt-1.5" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
